import { t } from "../../i18n";
import { useMemo } from "react";
import { Brain } from "lucide-react";
import {
  type AgentWorkspaceApproval,
  type Message,
  type MessageActivityEvent,
  type SubagentRunView,
  type ToolEvent,
} from "@lumen/shared";
import { ToolActivityCard } from "./ToolActivityCard";
import { SubagentDiscussion } from "../agent-workspaces/SubagentDiscussion";
import { ActivityEventCard } from "./ActivityEventCard";
export { ContextCompactionCard } from "./ContextCompactionCard";
export { MessageAttachments } from "./MessageAttachments";
import { ThinkingBlock } from "./ThinkingBlock";
import { useInteractionDisclosure } from "./MessageActivity.utils";

export type ApprovalHandler = (
  id: string,
  decision: "approved" | "denied",
  message?: string,
  options?: {
    allowScope?: "once" | "exact" | "pattern";
    pattern?: string;
    stop?: boolean;
  },
) => void;

export type QuestionAnswerHandler = (
  questionId: string,
  answers: string[],
  custom: boolean,
) => void | Promise<void>;

export interface MessageActivityProps {
  message: Message;
  busy?: boolean;
  approvals?: Map<string, AgentWorkspaceApproval>;
  decidingApprovalIds?: Set<string>;
  onApproval?: ApprovalHandler;
  onQuestionAnswer?: QuestionAnswerHandler;
  onContinue?: () => void;
  onSubagentContinue?: (run: SubagentRunView) => void;
}

export function MessageActivity({
  message,
  busy = false,
  approvals = new Map(),
  decidingApprovalIds = new Set(),
  onApproval,
  onQuestionAnswer,
  onContinue,
  onSubagentContinue,
}: MessageActivityProps) {
  const activityEvents = useMemo(
    () =>
      (message.activityEvents ?? []).map((event) =>
        event.type === "text" && event.contentRange
          ? {
              ...event,
              content: message.content.slice(
                event.contentRange.start,
                event.contentRange.end,
              ),
            }
          : event,
      ),
    [message.activityEvents, message.content],
  );
  const visibleToolEvents = useMemo(() => {
    const byId = new Map(
      (message.toolEvents ?? []).map((event) => [event.id, event]),
    );
    for (const event of activityEvents)
      if (event.type === "tool" && event.tool)
        byId.set(event.tool.id, event.tool);
    // Approvals load independently of the bounded conversation response.
    // Recover a usable card even when both event arrays lost the tool.
    const messageIds = new Set(message.activityMessageIds ?? [message.id]);
    const representedApprovals = new Set(
      [...byId.values()].map((event) => event.approvalId),
    );
    for (const approval of approvals.values()) {
      if (
        approval.status !== "pending" ||
        approval.conversationId !== message.conversationId ||
        !messageIds.has(approval.assistantMessageId) ||
        representedApprovals.has(approval.id)
      )
        continue;
      byId.set(`approval-${approval.id}`, {
        id: `approval-${approval.id}`,
        name: "agent_workspace_shell",
        status: "awaiting-approval",
        approvalId: approval.id,
        arguments: { command: approval.command, cwd: approval.cwd },
      });
    }
    return [...byId.values()];
  }, [activityEvents, approvals, message]);
  const displayActivityEvents = useMemo(() => {
    if (!activityEvents.length) return activityEvents;
    const placedTools = new Set(
      activityEvents.flatMap((event) =>
        event.type === "tool" && event.tool ? [event.tool.id] : [],
      ),
    );
    return [
      ...activityEvents,
      ...visibleToolEvents
        .filter(
          (tool) =>
            !placedTools.has(tool.id) &&
            (tool.status === "awaiting-approval" ||
              tool.status === "awaiting-question"),
        )
        .map((tool): MessageActivityEvent => ({
          id: `tool-${tool.id}`,
          type: "tool",
          tool,
        })),
    ];
  }, [activityEvents, visibleToolEvents]);
  const runsByToolEvent = useMemo(
    () => subagentRunsByToolEvent(message, displayActivityEvents),
    [message, displayActivityEvents],
  );
  const placedRunIds = useMemo(
    () => new Set([...runsByToolEvent.values()].map((run) => run.id)),
    [runsByToolEvent],
  );
  const unassignedRuns = useMemo(
    () =>
      (message.subagentRuns ?? []).filter((run) => !placedRunIds.has(run.id)),
    [message.subagentRuns, placedRunIds],
  );
  const continuationRunId = useMemo(() => {
    const lastProgressEvent = [...displayActivityEvents]
      .reverse()
      .find(isContinuationBlockingActivity);
    if (lastProgressEvent?.type === "tool")
      return runsByToolEvent.get(lastProgressEvent.id)?.id ?? null;
    if (!lastProgressEvent) return unassignedRuns.at(-1)?.id ?? null;
    return null;
  }, [displayActivityEvents, runsByToolEvent, unassignedRuns]);
  const activityNeedsAttention = visibleToolEvents.some(
    (event) =>
      event.status === "awaiting-approval" ||
      event.status === "awaiting-question",
  );
  const { open: activityOpen, onToggle: onActivityToggle } =
    useInteractionDisclosure(activityNeedsAttention);

  return (
    <>
      {!displayActivityEvents.length &&
        (Boolean(message.reasoning) || visibleToolEvents.length > 0) && (
          <details
            open={activityOpen}
            onToggle={onActivityToggle}
            className="mb-3 rounded-xl border border-stone-200 bg-stone-50/70 text-xs dark:border-white/10 dark:bg-white/[0.03]"
          >
            <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 font-semibold text-stone-500 marker:content-none">
              <Brain size={14} />
              <span>{t("Model activity")}</span>
              {visibleToolEvents.length > 0 && (
                <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
                  {t("tool", { count: visibleToolEvents.length })}
                  {visibleToolEvents.length === 1 ? "call" : "calls"}
                </span>
              )}
            </summary>
            {activityOpen && (
              <div className="space-y-3 border-t border-stone-200 p-3 dark:border-white/10">
                {message.reasoning && (
                  <ThinkingBlock content={message.reasoning} streaming={busy} />
                )}
                {visibleToolEvents.map((event) => (
                  <ToolActivityCard
                    key={event.id}
                    event={event}
                    approval={
                      event.approvalId
                        ? approvals.get(event.approvalId)
                        : undefined
                    }
                    busy={
                      busy ||
                      Boolean(
                        event.approvalId &&
                        decidingApprovalIds.has(event.approvalId),
                      )
                    }
                    onApproval={onApproval}
                    onContinue={onContinue}
                    onQuestionAnswer={onQuestionAnswer}
                  />
                ))}
              </div>
            )}
          </details>
        )}
      {displayActivityEvents.length > 0 && (
        <div className="space-y-3">
          {displayActivityEvents.map((event) => {
            const run = runsByToolEvent.get(event.id);
            if (run)
              return (
                <SubagentDiscussion
                  key={event.id}
                  run={run}
                  approvals={approvals}
                  busy={busy}
                  decidingApprovalIds={decidingApprovalIds}
                  onApproval={onApproval}
                  onQuestionAnswer={onQuestionAnswer}
                  onSubagentContinue={
                    run.id === continuationRunId && onSubagentContinue
                      ? () => onSubagentContinue(run)
                      : undefined
                  }
                />
              );
            return (
              <ActivityEventCard
                key={event.id}
                event={event}
                busy={busy}
                approvals={approvals}
                decidingApprovalIds={decidingApprovalIds}
                onApproval={onApproval}
                onContinue={onContinue}
                onQuestionAnswer={onQuestionAnswer}
              />
            );
          })}
        </div>
      )}
      {unassignedRuns.length > 0 && (
        <div className="mt-4 space-y-3">
          {unassignedRuns.map((run) => (
            <SubagentDiscussion
              key={run.id}
              run={run}
              approvals={approvals}
              busy={busy}
              decidingApprovalIds={decidingApprovalIds}
              onApproval={onApproval}
              onQuestionAnswer={onQuestionAnswer}
              onSubagentContinue={
                run.id === continuationRunId && onSubagentContinue
                  ? () => onSubagentContinue(run)
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </>
  );
}

function isContinuationBlockingActivity(event: MessageActivityEvent) {
  return event.type !== "error" && event.type !== "compaction";
}

function subagentRunIdFromTool(event: ToolEvent) {
  if (
    !event.result ||
    typeof event.result !== "object" ||
    !("subagentRunId" in event.result)
  )
    return null;
  return typeof event.result.subagentRunId === "string"
    ? event.result.subagentRunId
    : null;
}

function subagentRunsByToolEvent(
  message: Message,
  activityEvents: MessageActivityEvent[] = message.activityEvents ?? [],
) {
  const runs = message.subagentRuns ?? [];
  const byId = new Map(runs.map((run) => [run.id, run]));
  const assigned = new Set<string>();
  const result = new Map<string, SubagentRunView>();
  const delegationEvents = activityEvents.filter(
    (event): event is Extract<MessageActivityEvent, { type: "tool" }> =>
      event.type === "tool" &&
      Boolean(event.tool?.name?.startsWith("delegate_")),
  );
  for (const event of delegationEvents) {
    const runId = subagentRunIdFromTool(event.tool);
    const run = runId ? byId.get(runId) : undefined;
    if (run) {
      result.set(event.id, run);
      assigned.add(run.id);
    }
  }
  const unassignedRuns = runs.filter((run) => !assigned.has(run.id));
  let runIndex = 0;
  for (const event of delegationEvents) {
    if (result.has(event.id) || runIndex >= unassignedRuns.length) continue;
    result.set(event.id, unassignedRuns[runIndex++]);
  }
  for (const event of delegationEvents) {
    if (result.has(event.id)) continue;
    const run = subagentRunSummaryFromTool(message, event.tool);
    if (run) result.set(event.id, run);
  }
  return result;
}

function subagentRunSummaryFromTool(
  message: Message,
  tool: ToolEvent,
): SubagentRunView | null {
  const id = subagentRunIdFromTool(tool);
  if (!id) return null;
  const result =
    tool.result && typeof tool.result === "object"
      ? (tool.result as Record<string, unknown>)
      : {};
  const argumentsValue =
    tool.arguments && typeof tool.arguments === "object"
      ? (tool.arguments as Record<string, unknown>)
      : {};
  const status =
    tool.status === "running" ||
    tool.status === "awaiting-approval" ||
    tool.status === "awaiting-question" ||
    tool.status === "failed"
      ? tool.status
      : "completed";
  const timestamp = tool.startedAt ?? message.createdAt;
  return {
    id,
    conversationId: message.conversationId,
    sourceMessageId: message.parentId ?? message.id,
    assistantMessageId: message.id,
    agentId: "",
    agentName:
      typeof result.agent === "string"
        ? result.agent
        : tool.name.slice("delegate_".length).replace(/_/g, " "),
    agentIcon: null,
    task: typeof argumentsValue.task === "string" ? argumentsValue.task : "",
    status,
    error: tool.error ?? null,
    modelId: null,
    modelName: null,
    modelRemoteId: null,
    createdAt: timestamp,
    updatedAt: tool.finishedAt ?? timestamp,
  };
}
